import React, { useEffect, useState } from "react";
import { BellRing, Plus, Trash2, Pause, Play, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatPrice } from "../lib/api";
import { useT } from "../lib/i18n";
import { PageHeader } from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";

const Alerts = () => {
  const { t } = useT();
  const FREQ = { direct: t("p2.freq.direct"), daily: t("p2.freq.daily"), weekly: t("p2.freq.weekly") };
  const [alerts, setAlerts] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ city: "", max_price: 2000, bedrooms: 1, frequency: "daily" });
  const [busy, setBusy] = useState(false);

  const load = () => api("/alerts").then((d) => setAlerts(d.alerts)).catch(() => setAlerts([]));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.city.trim()) return toast.error(t("p2.alerts.errCity"));
    setBusy(true);
    try {
      await api("/alerts", { method: "POST", body: JSON.stringify({ ...form, max_price: Number(form.max_price), bedrooms: Number(form.bedrooms) }) });
      setOpen(false);
      setForm({ city: "", max_price: 2000, bedrooms: 1, frequency: "daily" });
      load();
      toast.success(t("p2.alerts.created"));
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const togglePause = async (a) => {
    await api(`/alerts/${a.id}`, { method: "PATCH", body: JSON.stringify({ paused: !a.paused }) });
    load();
  };
  const remove = async (id) => { await api(`/alerts/${id}`, { method: "DELETE" }); load(); };

  return (
    <div>
      <PageHeader
        icon={BellRing}
        title={t("p2.alerts.title")}
        subtitle={t("p2.alerts.subtitle")}
        right={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full bg-white text-navy hover:bg-white/90" data-testid="alert-new">
                <Plus size={16} className="mr-2" /> {t("p2.alerts.new")}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-display">{t("p2.alerts.newTitle")}</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>{t("p2.alerts.city")}</Label>
                  <Input className="mt-2 rounded-xl" data-testid="alert-city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Amsterdam" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>{t("p2.alerts.maxprice")}</Label>
                    <Input className="mt-2 rounded-xl" type="number" value={form.max_price} onChange={(e) => setForm({ ...form, max_price: e.target.value })} />
                  </div>
                  <div>
                    <Label>{t("p2.alerts.minbeds")}</Label>
                    <Input className="mt-2 rounded-xl" type="number" value={form.bedrooms} onChange={(e) => setForm({ ...form, bedrooms: e.target.value })} />
                  </div>
                </div>
                <div>
                  <Label>{t("p2.alerts.freq")}</Label>
                  <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })}>
                    <SelectTrigger className="mt-2 rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(FREQ).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={create} disabled={busy} data-testid="alert-save" className="rounded-xl bg-brandblue hover:bg-brandblue/90">
                  {busy ? <Loader2 className="animate-spin" size={18} /> : t("p2.alerts.create")}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
        {alerts === null ? (
          <div className="space-y-3">{Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-24 animate-pulse rounded-2xl bg-slate-100" />)}</div>
        ) : alerts.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-border p-16 text-center" data-testid="alerts-empty">
            <BellRing className="mx-auto text-slate-300" size={44} />
            <p className="mt-4 text-slate-500">{t("p2.alerts.empty")}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {alerts.map((a) => (
              <div key={a.id} data-testid={`alert-${a.id}`} className="flex items-center justify-between gap-4 rounded-2xl border border-border bg-white p-5 shadow-sm">
                <div>
                  <div className="font-display text-lg font-bold text-navy">{a.city}</div>
                  <div className="mt-1 text-sm text-slate-500">
                    {formatPrice(a.max_price)} · {a.bedrooms}+ · {FREQ[a.frequency]}
                    {a.paused && <span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-500">{t("p2.alerts.paused")}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="icon" className="rounded-full" onClick={() => togglePause(a)} data-testid={`alert-pause-${a.id}`}>
                    {a.paused ? <Play size={16} /> : <Pause size={16} />}
                  </Button>
                  <Button variant="outline" size="icon" className="rounded-full text-danger" onClick={() => remove(a.id)} data-testid={`alert-delete-${a.id}`}>
                    <Trash2 size={16} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Alerts;
